import React, { useState, useEffect } from "react";
import {
    Autocomplete,
    Checkbox,
    ListItemText,
    TextField,
    Box,
    Tabs,
    Tab,
    Typography
} from "@mui/material";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import ButtonComponent from "../../Components/Common/button/Button";

const DataElementConfiguration = ({
    selectedData = [],     // This comes from parent (applicableDataElementOption)
    onSubmit,
    userId,
    children
}) => {
    const ALL_OPTION = "All Data Element";

    const [dataElements, setDataElements] = useState([]);
    const [localSelection, setLocalSelection] = useState([]);
    const [tabValue, setTabValue] = useState(0);

    // Fetch all available data elements for dropdown
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await ApiService.get(`/v1/data-element/get`);
                setDataElements(response.data || []);
            } catch (error) {
                console.error("Error fetching data elements:", error);
            }
        };
        fetchData();
    }, []);

    const OPTIONS = [ALL_OPTION, ...dataElements];

    // Important: Sync localSelection when parent selectedData changes
    useEffect(() => {
        setLocalSelection(selectedData || []);
    }, [selectedData]);

    // Reset tab index when selectedData changes
    useEffect(() => {
        if (selectedData.length > 0 && tabValue >= selectedData.length) {
            setTabValue(0);
        }
    }, [selectedData]);

    const handleChange = (event, newValue) => {
        if (newValue.includes(ALL_OPTION)) {
            const isAllCurrentlySelected = localSelection.length === dataElements.length;
            setLocalSelection(isAllCurrentlySelected ? [] : [...dataElements]);
            return;
        }
        setLocalSelection(newValue.filter((option) => option !== ALL_OPTION));
    };

    const isAllChecked = dataElements.length > 0 && localSelection.length === dataElements.length;
    const isValid = localSelection.length > 0;

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleSubmit = async () => {
        if (!isValid) return;

        const original = selectedData || [];
        const current = localSelection;

        const payload = {
            ADD: current.filter(item => !original.includes(item)),
            REMOVE: original.filter(item => !current.includes(item))
        };

        try {
            await ApiService.post(`v1/data-element/update?userId=${userId}`, payload);

            MessagePopup({
                icon: "success",
                message: "Data Elements updated successfully!",
            });

            onSubmit?.(localSelection);   // This sends updated list back to parent

        } catch (error) {
            console.error("Error while updating Data Elements:", error);
            MessagePopup({
                icon: "error",
                message: "Failed to update Data Elements!",
            });
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* Dropdown + Submit Button */}
            <Box sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                width: "100%"
            }}>
                <Box sx={{ flex: 1 }}>
                    <Autocomplete
                        multiple
                        options={OPTIONS}
                        value={localSelection}
                        onChange={handleChange}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox
                                    checked={option === ALL_OPTION ? isAllChecked : selected}
                                />
                                <ListItemText primary={option} />
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Data Elements Configuration"
                                error={!isValid}
                                helperText={!isValid ? "At least one Data Element is required" : ""}
                                InputLabelProps={{
                                    shrink: true,
                                    style: { transform: "translate(14px, -14px) scale(0.75)" }
                                }}
                                variant="outlined"
                                fullWidth
                                size="small"
                            />
                        )}
                    />
                </Box>

                <Box sx={{ pt: 0.5 }}>
                    <ButtonComponent
                        bgColor="black"
                        color="white"
                        label="Submit"
                        fullWidth
                        onClick={handleSubmit}
                        disabled={!isValid}
                    />
                </Box>
            </Box>

            {/* Tabs - Show based on selectedData from parent */}
            {selectedData.length > 0 && (
                <Box sx={{ marginTop: 2 }}>
                    {/* <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                        Configured Data Elements
                    </Typography> */}

                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                    >
                        {selectedData.map((element, index) => (
                            <Tab
                                key={index}
                                label={element}
                                sx={{ textTransform: "none", fontWeight: 500 }}
                            />
                        ))}
                    </Tabs>

                    {/* Tab Content */}
                    <Box sx={{ p: 0 }}>
                        {children?.[tabValue] || (
                            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                                No content available for this tab
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default DataElementConfiguration;